import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import api from '../../utils/api';
import { cardStyles } from '../../theme/componentStyles';
import useThemeStyles from '../../theme/useThemeStyles';

const questionnairePageBackgroundSx = {
  minHeight: '100vh',
  py: 4,
  px: 2,
  backgroundImage: cardStyles.gradient.backgroundImage,
  backgroundAttachment: 'fixed'
};

/** Coerce API options (may be missing, or legacy string entries) to { label, value } for display. */
function getFieldOptionsForDisplay(field) {
  const raw = field.options;
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((o, i) => {
    if (typeof o === 'string') {
      return { label: o, value: o };
    }
    const label = o?.label != null ? String(o.label) : '';
    const valueRaw = o?.value != null ? String(o.value) : '';
    const value = valueRaw !== '' ? valueRaw : (label || `option_${i + 1}`);
    return {
      label: label || value || `Option ${i + 1}`,
      value
    };
  });
}

function PublicQuestionnairePage() {
  const styles = useThemeStyles();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const honeypotRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/api/public/questionnaire/${token}`);
      setPayload(res.data);
      const initial = {};
      (res.data.fields || []).forEach((f) => {
        if (f.defaultValue) {
          initial[f.name] = f.defaultValue;
        } else if (f.type === 'checkbox' && (f.options || []).length > 0) {
          initial[f.name] = [];
        }
      });
      if (res.data.mode === 'share' && (res.data.missingContactFields || []).length > 0) {
        if ((res.data.missingContactFields || []).includes('name')) initial._contactName = '';
        if ((res.data.missingContactFields || []).includes('email')) initial._contactEmail = '';
      }
      setValues(initial);
    } catch (err) {
      setError(err.response?.data?.message || 'Questionnaire not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!payload) return;
    if (payload.mode === 'share') {
      const fieldNames = (payload.fields || []).map((f) => f.name);
      const hasName = fieldNames.includes('name');
      const hasEmail = fieldNames.includes('email');
      const nameVal = hasName ? values.name : values._contactName;
      const emailVal = hasEmail ? values.email : values._contactEmail;
      if (!String(nameVal || '').trim() || !String(emailVal || '').trim()) {
        setError('Please enter your name and email.');
        return;
      }
    }
    try {
      setSubmitting(true);
      setError('');
      const res = await api.post(`/api/public/questionnaire/${token}/submit`, {
        ...values,
        idotoo_hp: honeypotRef.current?.value || ''
      });
      setDone(true);
      setSuccessMessage(res.data?.message || payload.successMessage || 'Thank you for your responses.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={questionnairePageBackgroundSx}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !payload) {
    return (
      <Box sx={questionnairePageBackgroundSx}>
        <Box maxWidth={560} mx="auto" mt={6}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={questionnairePageBackgroundSx}>
        <Box maxWidth={560} mx="auto" mt={6}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      </Box>
    );
  }

  const primary = payload?.styling?.primaryColor || '#b56576';
  const showContactInject =
    payload?.mode === 'share' && (payload?.missingContactFields || []).length > 0;

  return (
    <Box
      sx={{
        ...questionnairePageBackgroundSx,
        fontFamily: payload?.styling?.fontFamily || 'system-ui, sans-serif'
      }}
    >
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        {payload?.logoUrl ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2.5
            }}
          >
            <Box
              component="img"
              src={payload.logoUrl}
              alt={payload.businessName ? `${payload.businessName} logo` : 'Business logo'}
              sx={{
                maxHeight: 80,
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>
        ) : null}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          {payload?.name}
        </Typography>
        {payload?.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {payload.description}
          </Typography>
        ) : null}
        {payload?.leadName ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            For: {payload.leadName}
          </Typography>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box component="form" onSubmit={handleSubmit}>
          <input
            ref={honeypotRef}
            type="text"
            name="idotoo_hp"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            defaultValue=""
            style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
          />
          {showContactInject ? (
            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(payload?.missingContactFields || []).includes('name') ? (
                <TextField
                  fullWidth
                  required
                  label="Your name"
                  value={values._contactName ?? ''}
                  onChange={(e) => handleChange('_contactName', e.target.value)}
                  autoComplete="name"
                />
              ) : null}
              {(payload?.missingContactFields || []).includes('email') ? (
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email address"
                  value={values._contactEmail ?? ''}
                  onChange={(e) => handleChange('_contactEmail', e.target.value)}
                  autoComplete="email"
                />
              ) : null}
            </Box>
          ) : null}
          {(payload?.fields || [])
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((field) => (
              <Box key={field.name} sx={{ mb: 2.5 }}>
                {field.type === 'textarea' ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label={field.label}
                    required={!!field.required}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  />
                ) : field.type === 'select' ? (
                  <TextField
                    fullWidth
                    select
                    label={field.label}
                    required={!!field.required}
                    value={values[field.name] ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {getFieldOptionsForDisplay(field).map((opt, idx) => (
                      <MenuItem key={`${field.name}-opt-${idx}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.type === 'radio' ? (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">{field.label}</FormLabel>
                    <RadioGroup
                      value={values[field.name] ?? ''}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                    >
                      {getFieldOptionsForDisplay(field).map((opt, idx) => (
                        <FormControlLabel
                          key={`${field.name}-opt-${idx}`}
                          value={opt.value}
                          control={<Radio required={!!field.required} />}
                          label={opt.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ) : field.type === 'checkbox' && (field.options || []).length > 0 ? (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">{field.label}</FormLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      {getFieldOptionsForDisplay(field).map((opt, idx) => {
                        const v = opt.value;
                        const selected = Array.isArray(values[field.name]) ? values[field.name] : [];
                        const checked = selected.includes(v);
                        return (
                          <FormControlLabel
                            key={`${field.name}-cb-${idx}`}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) => {
                                  const cur = Array.isArray(values[field.name]) ? [...values[field.name]] : [];
                                  if (e.target.checked) {
                                    if (!cur.includes(v)) cur.push(v);
                                  } else {
                                    const i = cur.indexOf(v);
                                    if (i >= 0) cur.splice(i, 1);
                                  }
                                  handleChange(field.name, cur);
                                }}
                              />
                            }
                            label={opt.label}
                          />
                        );
                      })}
                    </Box>
                  </FormControl>
                ) : field.type === 'checkbox' ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!values[field.name]}
                        onChange={(e) => handleChange(field.name, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                ) : (
                  <TextField
                    fullWidth
                    type={
                      field.type === 'email'
                        ? 'email'
                        : field.type === 'number'
                          ? 'number'
                          : field.type === 'date'
                            ? 'date'
                            : field.type === 'phone'
                              ? 'tel'
                              : 'text'
                    }
                    label={field.label}
                    required={!!field.required}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                  />
                )}
              </Box>
            ))}

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            fullWidth
            sx={{ mt: 2, py: 1.5, bgcolor: primary, '&:hover': { bgcolor: primary } }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : payload?.styling?.buttonText || 'Submit'}
          </Button>
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}

export default PublicQuestionnairePage;
